import {AdvertiserCampaignDevicesModel} from './advertiser-campaign-devices.model';
import {State} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {AdvertiserDevicesService} from '../../services-advertiser/devices/devices.service';
import {IDeviceAdvertiserCampaign} from '@interfaces/advertiser/devices';
import {finalize, tap} from 'rxjs/operators';
import {IResponse} from '@interfaces/response';
import {AdvertiserCampaignMediaState} from '../advertiser-campaign-media';
import {AdvertiserDevicesSortType} from '../../types/advertiser-devices.type';
import {OrderType} from '../../types/order.type';
import {AdvertiserCampaignsState} from '../advertiser-campaigns';
import {NgxsDataRepository} from "@angular-ru/ngxs/repositories";
import {Payload, StateRepository} from "@angular-ru/ngxs/decorators";
import {DataActionWithoutSubscription} from "@shared/functions";

@StateRepository()
@State<AdvertiserCampaignDevicesModel>({
  name: 'advertiserCampaignDevicesState',
  defaults: {
    itemsPerPage: 6,
    currentPage: 1,
    sort: 'id',
    order: 'DESC',
    search: '',
    devices: [],
    startDate: '',
    finishDate: '',
    showSelectedDevices: false,
    total: 0,
    selectedDevices: [],
    devicesToAdd: [],
    devicesToRemove: [],
    selectedDevicesFromDb: [],
    isDataLoading: false
  }
})

@Injectable()
export class AdvertiserCampaignDevicesState extends NgxsDataRepository<AdvertiserCampaignDevicesModel> {
  constructor(private deviceService: AdvertiserDevicesService,
              private campaignMediaState: AdvertiserCampaignMediaState,
              private campaignState: AdvertiserCampaignsState) {
    super();
  }

  @DataActionWithoutSubscription({cancelUncompleted: true})
  getDevices(@Payload('[Advertiser campaign devices] get devices')
               payload?: { mediaId: number, start: string, finish: string })
    : Observable<IResponse<IDeviceAdvertiserCampaign>> {
    const limit = this.ctx.getState().itemsPerPage;
    const skip = limit * (this.ctx.getState().currentPage - 1);
    const order = this.ctx.getState().order;
    const sort = this.ctx.getState().sort;
    const search = this.ctx.getState().search;
    const selectedMediaId = payload?.mediaId || this.campaignMediaState.snapshot.selectedMedia?.id;
    const start = payload?.start || this.ctx.getState().startDate;
    const finish = payload?.finish || this.ctx.getState().finishDate;
    const savedCampaignId = this.campaignState.snapshot.savedCampaignId || undefined;

    this.ctx.patchState({isDataLoading: true});

    return this.deviceService.getAdvertiserDevicesForCampaign({
      limit, skip, search, sort, order,
      mediaId: selectedMediaId, finish, start, campaignId: savedCampaignId
    }).pipe(
      tap((devices) => {
        this.ctx.setState({
          ...this.ctx.getState(),
          devices: devices.rows, total: devices.count,
          startDate: start, finishDate: finish, isDataLoading: false
        });
        this.saveSelectedDevices({devices: devices.rows});
      }),
      finalize(() => this.ctx.patchState({isDataLoading: false}))
    );
  }

  @DataActionWithoutSubscription()
  getSelectedDevicesActualInfo(@Payload('[Advertiser campaign devices] search devices') payload: {
    mediaId: number, ids: number[], start: string, finish: string, savedCampaignId?: number
  }): Observable<any> {
    return this.deviceService.getSelectedDevicesActualInfo({
      mediaId: payload.mediaId, start: payload.start,
      finish: payload.finish, savedCampaignId: payload.savedCampaignId, dIds: payload.ids
    }).pipe(
      tap((data) => {
        this.ctx.patchState({startDate: payload.start, finishDate: payload.finish});
        this.saveSelectedDevices({devices: data.rows});
      })
    );
  }

  @DataActionWithoutSubscription()
  searchDevices(@Payload('[Advertiser campaign devices] search devices') search: string): Observable<any> {
    this.ctx.patchState({search, currentPage: 1});
    return this.getDevices();
  }

  @DataActionWithoutSubscription()
  setSelectedDevicesByEvent(@Payload('[Advertiser campaign devices] set selected devices by checkbox event')
                              payload: { event: Event, device: IDeviceAdvertiserCampaign }): void {
    const isChecked = (payload.event.target as HTMLInputElement).checked;
    const state = this.ctx.getState();
    if (isChecked) {
      this.ctx.patchState({selectedDevices: [payload.device, ...state.selectedDevices]});
      this.setDeviceToAdd(payload.device.id);
    } else {
      this.ctx.patchState({
        selectedDevices: state.selectedDevices.filter((item) => item.id !== payload.device.id)
      });
      this.setDeviceToRemove(payload.device.id);
    }
  }

  @DataActionWithoutSubscription()
  saveSelectedDevices(@Payload('[Advertiser campaign devices] save selected devices')
                        payload: { devices: IDeviceAdvertiserCampaign[], start?: string, finish?: string }): void {
    const selectedIdsFromDb = payload.devices.filter((device) => device.savedForCreation)
      .map((device) => device.id);
    const selectedIdsFromStore = this.ctx.getState().selectedDevices.map((device) => device.id);

    const devicesToUpdate = payload.devices.filter((device) => {
      return (selectedIdsFromStore.includes(device.id) || device.savedForCreation)
        && !this.ctx.getState().devicesToRemove.includes(device.id);
    });
    const devicesWithOldData = this.ctx.getState().selectedDevices.filter((device) => {
      return !devicesToUpdate.map((d) => d.id).includes(device.id);
    });

    const start = payload?.start || this.ctx.getState().startDate;
    const finish = payload?.finish || this.ctx.getState().finishDate;

    this.ctx.patchState({
      selectedDevicesFromDb: [...new Set([...this.ctx.getState().selectedDevicesFromDb, ...selectedIdsFromDb])],
      selectedDevices: [...devicesToUpdate, ...devicesWithOldData], startDate: start, finishDate: finish
    });
  }

  @DataActionWithoutSubscription()
  cleanSelectedDevices(): void {
    const idsFromDb = this.ctx.getState().selectedDevicesFromDb;
    this.ctx.patchState({selectedDevices: [], devicesToRemove: idsFromDb});
  }

  @DataActionWithoutSubscription()
  setPage(@Payload('[Advertiser campaign devices] set page') page: number): Observable<any> {
    this.ctx.patchState({currentPage: page});
    return this.getDevices();
  }

  @DataActionWithoutSubscription()
  setDeviceToAdd(@Payload('[Advertiser campaign devices] set device to add') deviceId: number): void {
    const isIdExistInDB = this.ctx.getState().selectedDevicesFromDb.includes(deviceId);

    this.ctx.patchState({
      devicesToRemove: this.ctx.getState().devicesToRemove.filter((id) => id !== deviceId)
    });

    if (!isIdExistInDB) {
      this.ctx.patchState({
        devicesToAdd: [...new Set([...this.ctx.getState().devicesToAdd, deviceId])],
      });
    }
  }

  @DataActionWithoutSubscription()
  setDeviceToRemove(@Payload('[Advertiser campaign devices] set device to remove') deviceId: number): void {
    const isIdExistInDB = this.ctx.getState().selectedDevicesFromDb.includes(deviceId);

    this.ctx.patchState({
      devicesToAdd: this.ctx.getState().devicesToAdd.filter((id) => id !== deviceId)
    });

    if (isIdExistInDB) {
      this.ctx.patchState({
        devicesToRemove: [...new Set([...this.ctx.getState().devicesToRemove, deviceId])],
      });
    }
  }

  @DataActionWithoutSubscription()
  toggleSelectedDevicesDisplaying(@Payload('[Advertiser campaign devices] show or hide selected devices')
                                    event: Event): void {
    const show = (event.target as HTMLInputElement).checked;
    this.ctx.patchState({showSelectedDevices: show});
  }

  @DataActionWithoutSubscription()
  sortDevices(@Payload('[Advertiser campaign devices] sort devices')
                payload: { sort: AdvertiserDevicesSortType, order: OrderType }): Observable<any> {
    this.ctx.patchState({sort: payload.sort, order: payload.order});
    return this.getDevices();
  }

  @DataActionWithoutSubscription()
  showSelectedDevicesTab(): void {
    this.ctx.patchState({showSelectedDevices: true});
  }
}
